import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, BACKEND_URL } from "../lib/api";
import Navbar from "../components/Navbar";
import TemplateRenderer from "../components/TemplateRenderer";
import ImageUpload from "../components/ImageUpload";
import GalleryUpload from "../components/GalleryUpload";
import IntroPicker from "../components/IntroPicker";
import { toast } from "sonner";

export default function Editor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [inv, setInv] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [mobileTab, setMobileTab] = useState("customize"); // customize | preview

  useEffect(() => {
    api.get(`/invitations/${id}`).then((r) => setInv(r.data)).catch(() => nav("/dashboard"));
    api.get("/templates").then((r) => setTemplates(r.data));
  }, [id, nav]);

  if (!inv) return <div className="min-h-screen flex items-center justify-center text-stone-500">Loading editor...</div>;

  const setData = (key, value) => setInv({ ...inv, data: { ...inv.data, [key]: value } });
  const renameSlug = async () => {
    const next = (window.prompt("New invitation link (a-z, 0-9, dashes):", inv.slug) || "").trim();
    if (!next || next === inv.slug) return;
    try {
      const r = await api.put(`/invitations/${id}/slug`, { slug: next });
      setInv({ ...inv, slug: r.data.slug });
      toast.success(`Link updated → /i/${r.data.slug}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not rename link");
    }
  };
  const setEvent = (i, key, val) => {
    const events = [...inv.data.events];
    events[i] = { ...events[i], [key]: val };
    setData("events", events);
  };
  const addEvent = () => setData("events", [...inv.data.events, { name: "New Event", date: "Day", time: "Time", venue: "Venue", address: "" }]);
  const removeEvent = (i) => setData("events", inv.data.events.filter((_, x) => x !== i));
  const setGallery = (i, val) => {
    const g = [...inv.data.gallery];
    g[i] = val;
    setData("gallery", g);
  };
  const addGallery = () => setData("gallery", [...inv.data.gallery, "https://images.unsplash.com/photo-1606216794074-735e91aa2c92?w=900&q=80"]);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/invitations/${id}`, { title: inv.title, template_id: inv.template_id, data: inv.data });
      setInv(r.data);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const fld = "w-full px-3 py-2 bg-white border border-stone-300 outline-none focus:border-stone-900 text-sm";

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="editor-page">
      <Navbar />

      {/* Mobile tabs — switch between Customize and Preview on small screens */}
      <div className="lg:hidden sticky top-16 z-30 bg-[#FDFBF7] border-b border-stone-200 flex">
        <button onClick={() => setMobileTab("customize")} data-testid="mobile-tab-customize" className={`flex-1 py-3 text-xs uppercase tracking-[0.3em] ${mobileTab === "customize" ? "bg-stone-900 text-white" : "text-stone-700"}`}>✎ Customize</button>
        <button onClick={() => setMobileTab("preview")} data-testid="mobile-tab-preview" className={`flex-1 py-3 text-xs uppercase tracking-[0.3em] ${mobileTab === "preview" ? "bg-stone-900 text-white" : "text-stone-700"}`}>◉ Preview</button>
      </div>

      <div className="px-4 lg:px-8 py-4 lg:py-6 grid lg:grid-cols-12 gap-4 lg:gap-6">
        {/* Editor sidebar */}
        <div className={`lg:col-span-4 xl:col-span-3 bg-white border border-stone-200 p-4 lg:p-6 lg:max-h-[calc(100vh-100px)] lg:overflow-y-auto no-scrollbar ${mobileTab === "customize" ? "block" : "hidden lg:block"}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif-display text-2xl">Customize</h2>
            <button onClick={save} disabled={saving} data-testid="editor-save" className="px-4 py-2 bg-stone-900 text-white text-xs uppercase tracking-[0.2em]">{saving ? "Saving..." : "Save"}</button>
          </div>

          <div className="space-y-3 text-sm">
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Project Title</label>
            <input data-testid="editor-title" value={inv.title} onChange={(e) => setInv({ ...inv, title: e.target.value })} className={fld} />

            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mt-4">Public Link</label>
            <div className="flex items-center gap-2">
              <div className={`${fld} font-mono-disp text-xs flex-1 bg-stone-50 truncate`} title={`/i/${inv.slug}`}>/i/{inv.slug}</div>
              <button type="button" onClick={renameSlug} data-testid="editor-rename-slug" className="px-3 py-2 border border-stone-900 text-xs uppercase tracking-[0.2em] hover:bg-stone-100 whitespace-nowrap">Rename</button>
            </div>

            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mt-4">Template</label>
            <select data-testid="editor-template" value={inv.template_id} onChange={(e) => setInv({ ...inv, template_id: e.target.value })} className={fld}>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>

            <div className="h-px bg-stone-200 my-4" />

            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Groom Name (short)</label>
            <input data-testid="editor-groom" value={inv.data.groom_name} onChange={(e) => setData("groom_name", e.target.value)} className={fld} />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Groom Full Name</label>
            <input value={inv.data.groom_full || ""} onChange={(e) => setData("groom_full", e.target.value)} className={fld} />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Groom Parents</label>
            <input value={inv.data.groom_parents || ""} onChange={(e) => setData("groom_parents", e.target.value)} className={fld} />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Groom Photo</label>
            <ImageUpload value={inv.data.groom_photo} onChange={(u) => setData("groom_photo", u)} label="Groom" aspect="aspect-[3/4]" />

            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mt-4">Bride Name (short)</label>
            <input data-testid="editor-bride" value={inv.data.bride_name} onChange={(e) => setData("bride_name", e.target.value)} className={fld} />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Bride Full Name</label>
            <input value={inv.data.bride_full || ""} onChange={(e) => setData("bride_full", e.target.value)} className={fld} />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Bride Parents</label>
            <input value={inv.data.bride_parents || ""} onChange={(e) => setData("bride_parents", e.target.value)} className={fld} />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Bride Photo</label>
            <ImageUpload value={inv.data.bride_photo} onChange={(u) => setData("bride_photo", u)} label="Bride" aspect="aspect-[3/4]" />

            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mt-4">Tagline</label>
            <input value={inv.data.tagline} onChange={(e) => setData("tagline", e.target.value)} className={fld} />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Hashtag</label>
            <input value={inv.data.hashtag || ""} onChange={(e) => setData("hashtag", e.target.value)} className={fld} placeholder="#YouWedsThem" />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Background Music URL (mp3)</label>
            <input value={inv.data.music_url || ""} onChange={(e) => setData("music_url", e.target.value)} className={fld} placeholder="https://.../song.mp3 (optional)" />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Wedding Date & Time</label>
            <input data-testid="editor-date" type="datetime-local" value={inv.data.wedding_date.slice(0, 16)} onChange={(e) => setData("wedding_date", new Date(e.target.value).toISOString())} className={fld} />

            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mt-4">Hero Background Photo</label>
            <ImageUpload value={inv.data.hero_image} onChange={(u) => setData("hero_image", u)} label="Hero" aspect="aspect-video" />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Save-the-Date Video URL <span className="normal-case text-[10px] text-stone-400">(MP4 / YouTube / Vimeo · loops on hero)</span></label>
            <input value={inv.data.hero_video_url || ""} onChange={(e) => setData("hero_video_url", e.target.value)} className={fld} placeholder="https://.../save-the-date.mp4 (optional)" data-testid="editor-hero-video" />

            <label className="flex items-center gap-3 mt-3 cursor-pointer select-none p-3 border border-stone-200 hover:bg-stone-50 transition-colors">
              <input type="checkbox" checked={inv.data.cinematic_seal_enabled !== false} onChange={(e) => setData("cinematic_seal_enabled", e.target.checked)} data-testid="editor-cinematic-seal-toggle" className="w-4 h-4 accent-stone-900" />
              <span className="text-sm">
                <span className="block font-medium">Show Cinematic Save-the-Date Seal</span>
                <span className="text-xs text-stone-500">Hanging lights + rotating wax seal + couple silhouette</span>
              </span>
            </label>

            <label className="flex items-center gap-3 mt-2 cursor-pointer select-none p-3 border border-stone-200 hover:bg-stone-50 transition-colors">
              <input type="checkbox" checked={inv.data.guestbook_enabled !== false} onChange={(e) => setData("guestbook_enabled", e.target.checked)} data-testid="editor-guestbook-toggle" className="w-4 h-4 accent-stone-900" />
              <span className="text-sm flex-1">
                <span className="block font-medium">Show Guestbook / Photo-wall</span>
                <span className="text-xs text-stone-500">Guests leave messages + photos; you approve from dashboard</span>
              </span>
              <Link to={`/guestbook/${id}`} data-testid="editor-guestbook-link" className="text-xs uppercase tracking-[0.2em] text-rose-700 underline underline-offset-4" onClick={(e) => e.stopPropagation()}>Moderate →</Link>
            </label>

            <div className="h-px bg-stone-200 my-4" />
            <IntroPicker
              value={inv.data.intro_variant || "default"}
              onChange={(v) => setData("intro_variant", v)}
              accent={(templates.find((t) => t.id === inv.template_id)?.palette?.accent) || "#B89B5E"}
            />
            <button
              type="button"
              onClick={() => { sessionStorage.removeItem("intro_seen_v2"); window.open(`/i/${inv.slug}`, "_blank"); }}
              className="mt-2 text-xs text-stone-600 hover:text-stone-900 underline underline-offset-4"
              data-testid="editor-preview-intro"
            >Preview opening animation in a new tab →</button>
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Save the Date Photo</label>
            <ImageUpload value={inv.data.save_the_date_image} onChange={(u) => setData("save_the_date_image", u)} label="Save the Date" aspect="aspect-video" />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Cover (Scratch reveal) Photo</label>
            <ImageUpload value={inv.data.cover_image} onChange={(u) => setData("cover_image", u)} label="Cover" aspect="aspect-video" />
            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500">Scratch Reveal Hint</label>
            <input value={inv.data.scratch_message} onChange={(e) => setData("scratch_message", e.target.value)} className={fld} />

            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mt-4">Our Story</label>
            <textarea rows={4} value={inv.data.story} onChange={(e) => setData("story", e.target.value)} className={fld} />

            <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mt-4">RSVP Message</label>
            <textarea rows={2} value={inv.data.message} onChange={(e) => setData("message", e.target.value)} className={fld} />

            <div className="h-px bg-stone-200 my-4" />
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-stone-500">Order of the Day</span>
              <button onClick={() => setData("order_of_day", [...(inv.data.order_of_day || []), { time: "", title: "", description: "" }])} className="text-xs underline">+ Add</button>
            </div>
            {(inv.data.order_of_day || []).map((o, i) => (
              <div key={i} className="border border-stone-200 p-3 space-y-2">
                <input value={o.time || ""} onChange={(e) => { const arr=[...inv.data.order_of_day]; arr[i]={...arr[i],time:e.target.value}; setData("order_of_day", arr); }} placeholder="Time (e.g. 5:00 PM)" className={fld} />
                <input value={o.title || ""} onChange={(e) => { const arr=[...inv.data.order_of_day]; arr[i]={...arr[i],title:e.target.value}; setData("order_of_day", arr); }} placeholder="Title (e.g. Ceremony)" className={fld} />
                <textarea rows={2} value={o.description || ""} onChange={(e) => { const arr=[...inv.data.order_of_day]; arr[i]={...arr[i],description:e.target.value}; setData("order_of_day", arr); }} placeholder="Short description" className={fld} />
                <button onClick={() => setData("order_of_day", inv.data.order_of_day.filter((_, x) => x !== i))} className="text-[10px] text-red-600 uppercase tracking-[0.2em]">Remove</button>
              </div>
            ))}

            <div className="h-px bg-stone-200 my-4" />
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Dress Code</div>
            <input value={inv.data.dress_code?.title || ""} onChange={(e) => setData("dress_code", { ...(inv.data.dress_code||{}), title: e.target.value })} placeholder="Title (e.g. Formal Attire)" className={fld} />
            <textarea rows={2} value={inv.data.dress_code?.note || ""} onChange={(e) => setData("dress_code", { ...(inv.data.dress_code||{}), note: e.target.value })} placeholder="Main note shown to guests" className={fld} />
            <input value={inv.data.dress_code?.color_advice || ""} onChange={(e) => setData("dress_code", { ...(inv.data.dress_code||{}), color_advice: e.target.value })} placeholder="Color advice (optional)" className={fld} />

            <div className="h-px bg-stone-200 my-4" />
            <div className="text-xs uppercase tracking-[0.2em] text-stone-500">Gifts</div>
            <textarea rows={2} value={inv.data.gifts?.note || ""} onChange={(e) => setData("gifts", { ...(inv.data.gifts||{}), note: e.target.value })} placeholder="Gift note (e.g. 'Your presence is the greatest gift...')" className={fld} />
            <input value={inv.data.gifts?.upi || ""} onChange={(e) => setData("gifts", { ...(inv.data.gifts||{}), upi: e.target.value })} placeholder="UPI ID (optional, e.g. aarav@okhdfc)" className={fld} />
            <textarea rows={3} value={inv.data.gifts?.bank || ""} onChange={(e) => setData("gifts", { ...(inv.data.gifts||{}), bank: e.target.value })} placeholder="Bank account details (optional)" className={fld} />

            <div className="h-px bg-stone-200 my-4" />
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-stone-500">Story Timeline</span>
              <button onClick={() => setData("timeline", [...(inv.data.timeline || []), { date: "When", title: "Title", description: "Describe this moment...", image: "" }])} className="text-xs underline">+ Add</button>
            </div>
            {(inv.data.timeline || []).map((t, i) => (
              <div key={i} className="border border-stone-200 p-3 space-y-2">
                <input value={t.date || ""} onChange={(e) => { const arr = [...inv.data.timeline]; arr[i] = { ...arr[i], date: e.target.value }; setData("timeline", arr); }} placeholder="Date label (e.g. March 2022)" className={fld} />
                <input value={t.title || ""} onChange={(e) => { const arr = [...inv.data.timeline]; arr[i] = { ...arr[i], title: e.target.value }; setData("timeline", arr); }} placeholder="Title (e.g. How We Met)" className={fld} />
                <textarea rows={3} value={t.description || ""} onChange={(e) => { const arr = [...inv.data.timeline]; arr[i] = { ...arr[i], description: e.target.value }; setData("timeline", arr); }} placeholder="Story description" className={fld} />
                <ImageUpload value={t.image} onChange={(u) => { const arr = [...inv.data.timeline]; arr[i] = { ...arr[i], image: u }; setData("timeline", arr); }} label="Photo" aspect="aspect-video" />
                <button onClick={() => setData("timeline", inv.data.timeline.filter((_, x) => x !== i))} className="text-[10px] text-red-600 uppercase tracking-[0.2em]">Remove</button>
              </div>
            ))}

            <div className="h-px bg-stone-200 my-4" />
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-[0.2em] text-stone-500">Events</span>
              <button onClick={addEvent} className="text-xs underline">+ Add</button>
            </div>
            {inv.data.events.map((e, i) => (
              <div key={i} className="border border-stone-200 p-3 space-y-2">
                <input value={e.name} onChange={(ev) => setEvent(i, "name", ev.target.value)} placeholder="Event name" className={fld} />
                <div className="grid grid-cols-2 gap-2">
                  <input value={e.date} onChange={(ev) => setEvent(i, "date", ev.target.value)} placeholder="Day label" className={fld} />
                  <input value={e.time} onChange={(ev) => setEvent(i, "time", ev.target.value)} placeholder="Time" className={fld} />
                </div>
                <input value={e.venue} onChange={(ev) => setEvent(i, "venue", ev.target.value)} placeholder="Venue" className={fld} />
                <input value={e.address} onChange={(ev) => setEvent(i, "address", ev.target.value)} placeholder="Address" className={fld} />
                <ImageUpload value={e.image} onChange={(u) => setEvent(i, "image", u)} label={`${e.name} Photo`} aspect="aspect-video" />
                <button onClick={() => removeEvent(i)} className="text-[10px] text-red-600 uppercase tracking-[0.2em]">Remove</button>
              </div>
            ))}

            <div className="h-px bg-stone-200 my-4" />
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs uppercase tracking-[0.2em] text-stone-500">Gallery Photos</span>
              <span className="text-xs text-stone-400">{inv.data.gallery.length} photos</span>
            </div>
            <GalleryUpload images={inv.data.gallery} onChange={(arr) => setData("gallery", arr)} />

            <div className="h-px bg-stone-200 my-4" />
            <Link to={`/preview/${id}`} data-testid="editor-preview-btn" className="block text-center py-3 bg-stone-900 text-white text-xs uppercase tracking-[0.3em]">Open Full Preview →</Link>
            {inv.paid ? (
              <Link to={`/i/${inv.slug}`} target="_blank" rel="noreferrer" data-testid="editor-public-link-btn" className="block text-center py-3 bg-emerald-600 text-white text-xs uppercase tracking-[0.3em] mt-2 hover:bg-emerald-700">Open Public Link ↗</Link>
            ) : (
              <Link to={`/checkout/${id}`} data-testid="editor-publish-btn" className="block text-center py-3 bg-amber-500 text-stone-900 text-xs uppercase tracking-[0.3em] mt-2 hover:bg-amber-400 font-semibold">
                Publish Now →
              </Link>
            )}
          </div>
        </div>

        {/* Live preview */}
        <div className={`lg:col-span-8 xl:col-span-9 bg-white border border-stone-200 lg:max-h-[calc(100vh-100px)] overflow-y-auto ${mobileTab === "preview" ? "block" : "hidden lg:block"}`} data-testid="editor-live-preview">
          <TemplateRenderer templateId={inv.template_id} data={inv.data} slug={null} isPreview />
        </div>
      </div>
    </div>
  );
}
